import {
  INFO_POPUP_TYPE,
  InfoPopup,
  MayNull,
  MuiTextfield,
  NextImage,
  PopupTitle,
  useMessageContext
} from '@apeiron/temp'
import { useMktErrorHandling } from '@apeiron/wallet'
import { Box, ButtonBase, styled } from '@mui/material'
import RenameSVG from '@public/icons/icon-rename.svg'
import ApeironConfirmButton from '@src/components/share/apeiron/ApeironConfirmButton'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import useUpdateOrbitalTrackInviteSettings from '@src/hooks/graphql/useUpdateOrbitalTrackInviteSettings'
import { OrbitalTrackSetting } from '@src/types/orbitalTrack'
import * as R from 'ramda'
import { FC, useCallback, useState } from 'react'
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useForm
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const StyledInfoPopup = styled(InfoPopup)`
  max-width: 500px;
  width: 95%;
`

const Form = styled('form')`
  display: flex;
  padding-bottom: 50px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding-right: 15px;
  padding-left: 15px;
`

const StyledPopupTitle = styled(PopupTitle)`
  width: 90%;
  margin-bottom: 15px;
`

const Container = styled(Box)`
  display: flex;
  gap: 10px;
  margin-bottom: 5px;
  width: 100%;
`

const InputName = styled(MuiTextfield)`
  width: 100%;
`

const StyledNextImage = styled(NextImage)`
  width: 18px;
  aspect-ratio: 1;
`

const ChangeGroupNameButtonWithPopup: FC<Props> = (props: Props) => {
  const { setting } = props

  const { t } = useTranslation()

  const [open, setOpen] = useState(false)

  const { star, refetchStar } = useOrbitalTrackAttachContext()

  const { mutate: updateInviteSetting } = useUpdateOrbitalTrackInviteSettings()

  const { showGeneralError, setInfo } = useMessageContext()

  const { showErrorPopup } = useMktErrorHandling()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty }
  } = useForm({ mode: 'onChange' })

  const handleOnClose = useCallback(() => {
    setOpen(false)
    reset()
  }, [reset])

  const doSubmit = useCallback(
    async (data: Record<string, any>) => {
      const { name } = data

      try {
        const success = await updateInviteSetting({
          starID: star?.id || '',
          settings: [
            {
              inviteGroupID: setting?.inviteGroupId || '',
              groupName: name
            }
          ]
        })

        if (success) {
          setInfo({
            message: t('godiverse.orbital_track.edit_name_success'),
            confirm: t('common.popup.ok')
          })

          refetchStar()

          setOpen(false)
        } else {
          showGeneralError(t('error.graphql.fallback.update_info'))
        }
      } catch (error) {
        showErrorPopup(error)
      }
    },
    [
      updateInviteSetting,
      star?.id,
      setting?.inviteGroupId,
      setInfo,
      t,
      refetchStar,
      showGeneralError,
      showErrorPopup
    ]
  )

  return (
    <>
      <ButtonBase onClick={() => setOpen(true)}>
        <StyledNextImage src={RenameSVG} />
      </ButtonBase>
      <StyledInfoPopup
        open={open}
        type={INFO_POPUP_TYPE.BLACK}
        onClose={handleOnClose}
      >
        <Form onSubmit={handleSubmit(doSubmit)}>
          <StyledPopupTitle title={t('godiverse.orbital_track.edit_name')} />
          <Container>
            <Controller
              name='name'
              control={control}
              defaultValue={setting?.groupName}
              rules={{
                required: true,
                minLength: 1
              }}
              render={(props: {
                field: ControllerRenderProps<any, 'name'>
                fieldState: ControllerFieldState
              }) => {
                const {
                  field,
                  fieldState: { error }
                } = props

                return <InputName {...field} error={!R.isNil(error)} />
              }}
            />
          </Container>
          <ApeironConfirmButton disabled={!isDirty} type='submit' />
        </Form>
      </StyledInfoPopup>
    </>
  )
}

type Props = {
  setting?: MayNull<OrbitalTrackSetting>
}

export default ChangeGroupNameButtonWithPopup
